/**
 * MyFoodDocuments — Feature 3 (v49.4)
 * Shows the documents (permit, hygiene, other) attached to a restaurant shop.
 * Clicking a document opens it inline in a full-screen modal (PDF or image).
 *
 * Rendered only when shop.category === 'myfood' && shop.documents?.length.
 * Also exports a small compact badge component `MyFoodDocsBadge`.
 */
import React, { useState } from 'react';
import { FileText, X, ChevronDown, ChevronUp, ShieldCheck } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { getMediaUrl } from '../utils/mediaUrl';

const D_L = {
  fr: { title: 'Documents officiels', badge: 'Documents vérifiés', open: 'Ouvrir', close: 'Fermer', permit: "Permis d'exploitation", hygiene: 'Hygiène alimentaire', other: 'Autre document' },
  en: { title: 'Official documents', badge: 'Verified documents', open: 'Open', close: 'Close', permit: 'Business permit', hygiene: 'Food hygiene', other: 'Other document' },
  es: { title: 'Documentos oficiales', badge: 'Documentos verificados', open: 'Abrir', close: 'Cerrar', permit: 'Permiso de explotación', hygiene: 'Higiene alimentaria', other: 'Otro documento' },
  pt: { title: 'Documentos oficiais', badge: 'Documentos verificados', open: 'Abrir', close: 'Fechar', permit: 'Alvará de exploração', hygiene: 'Higiene alimentar', other: 'Outro documento' },
  de: { title: 'Offizielle Dokumente', badge: 'Verifizierte Dokumente', open: 'Öffnen', close: 'Schließen', permit: 'Betriebsgenehmigung', hygiene: 'Lebensmittelhygiene', other: 'Anderes Dokument' },
  it: { title: 'Documenti ufficiali', badge: 'Documenti verificati', open: 'Apri', close: 'Chiudi', permit: 'Licenza di esercizio', hygiene: 'Igiene alimentare', other: 'Altro documento' },
  ru: { title: 'Официальные документы', badge: 'Проверенные документы', open: 'Открыть', close: 'Закрыть', permit: 'Разрешение на деятельность', hygiene: 'Пищевая гигиена', other: 'Другой документ' },
  ar: { title: 'الوثائق الرسمية', badge: 'وثائق موثقة', open: 'فتح', close: 'إغلاق', permit: 'رخصة الاستغلال', hygiene: 'النظافة الغذائية', other: 'وثيقة أخرى' },
  zh: { title: '官方文件', badge: '已验证文件', open: '打开', close: '关闭', permit: '营业许可证', hygiene: '食品卫生', other: '其他文件' },
  ja: { title: '公式書類', badge: '認証済み書類', open: '開く', close: '閉じる', permit: '営業許可証', hygiene: '食品衛生', other: 'その他の書類' },
  hi: { title: 'आधिकारिक दस्तावेज़', badge: 'सत्यापित दस्तावेज़', open: 'खोलें', close: 'बंद करें', permit: 'व्यवसाय परमिट', hygiene: 'खाद्य स्वच्छता', other: 'अन्य दस्तावेज़' },
  nl: { title: 'Officiële documenten', badge: 'Geverifieerde documenten', open: 'Openen', close: 'Sluiten', permit: 'Uitbatingsvergunning', hygiene: 'Voedselveiligheid', other: 'Ander document' },
  sw: { title: 'Nyaraka rasmi', badge: 'Nyaraka zilizothibitishwa', open: 'Fungua', close: 'Funga', permit: 'Kibali cha biashara', hygiene: 'Usafi wa chakula', other: 'Nyaraka nyingine' },
  ht: { title: 'Dokiman ofisyèl', badge: 'Dokiman verifye', open: 'Louvri', close: 'Fèmen', permit: "Pèmi eksplwatasyon", hygiene: 'Ijyèn alimantè', other: 'Lòt dokiman' },
};

const kindLabel = (k, l) => ({ permit: l.permit, hygiene: l.hygiene, other: l.other }[k] || l.other);
const isPdf = (url) => (url || '').toLowerCase().endsWith('.pdf');

export const MyFoodDocsBadge = ({ shop }) => {
  const { language } = useLanguage();
  const l = D_L[language] || D_L.fr;
  const has = shop?.category === 'myfood' && (shop?.has_documents || (shop?.documents || []).length > 0);
  if (!has) return null;
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-500/20 text-emerald-300 border border-emerald-500/30" data-testid="myfood-verified-badge">
      <ShieldCheck className="w-3 h-3" /> {l.badge}
    </span>
  );
};

const MyFoodDocuments = ({ shop }) => {
  const { language } = useLanguage();
  const l = D_L[language] || D_L.fr;
  const [openId, setOpenId] = useState(null);
  const [expanded, setExpanded] = useState(true);

  if (!shop || shop.category !== 'myfood') return null;
  const docs = shop.documents || [];
  if (docs.length === 0) return null;

  const openDoc = docs.find(d => d.id === openId);

  return (
    <section className="p-3 rounded-2xl bg-white/5 border border-white/10" data-testid="myfood-docs-section">
      <button type="button" onClick={() => setExpanded(v => !v)} className="w-full flex items-center justify-between gap-2 text-left">
        <span className="text-sm font-semibold flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-emerald-300" /> {l.title}
          <MyFoodDocsBadge shop={shop} />
        </span>
        {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>
      {expanded && (
        <div className="mt-2 space-y-1.5">
          {docs.map(d => (
            <button
              key={d.id}
              type="button"
              onClick={() => setOpenId(d.id)}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-left text-xs"
              data-testid={`myfood-doc-open-${d.id}`}
            >
              <FileText className="w-4 h-4 text-emerald-300 shrink-0" />
              <span className="flex-1 min-w-0 truncate">
                <span className="font-semibold">{d.title}</span>
                <span className="ml-2 text-white/40">· {kindLabel(d.kind, l)}</span>
              </span>
              <span className="text-emerald-300 shrink-0">{l.open}</span>
            </button>
          ))}
        </div>
      )}

      {openId && openDoc && (
        <div className="fixed inset-0 z-[9999] bg-black/90 backdrop-blur-sm flex items-center justify-center p-2" onClick={() => setOpenId(null)} data-testid="myfood-doc-modal">
          <div className="relative w-full max-w-3xl h-[85vh] bg-gray-900 rounded-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-3 bg-black/50">
              <p className="text-sm font-semibold truncate">{openDoc.title}</p>
              <button onClick={() => setOpenId(null)} className="p-1.5 rounded-full hover:bg-white/10"><X className="w-5 h-5" /></button>
            </div>
            {isPdf(openDoc.url) ? (
              <iframe src={getMediaUrl(openDoc.url)} title={openDoc.title} className="w-full h-full bg-white" />
            ) : (
              <div className="w-full h-full flex items-center justify-center overflow-auto bg-black/50">
                <img src={getMediaUrl(openDoc.url)} alt={openDoc.title} className="max-w-full max-h-full object-contain" />
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
};

export default MyFoodDocuments;
